/** Til betaling shows a completed visit only while reception still has money to take. */
export interface PaymentQueueRow {
  appointment_id: string;
  invoice_id: string;
  invoice_number: string | null;
  invoice_status: string;
  service_name: string | null;
  completed_at: string | null;
  starts_at: string;
  outstanding_cents: number;
  total_cents: number;
  due_at: string | null;
  patient_name: string | null;
}

export interface AwaitingCompletionRow {
  appointment_id: string;
  service_name: string | null;
  starts_at: string;
  ends_at: string;
  payment_status: string | null;
  patient_name: string | null;
}

export interface ReceptionDesk {
  queue: PaymentQueueRow[];
  awaiting: AwaitingCompletionRow[];
}

export const DESK_PAYMENT_METHODS = ['card', 'cash', 'mobilepay', 'bank_transfer', 'other'] as const;

export type DeskPaymentMethod = (typeof DESK_PAYMENT_METHODS)[number];

export function splitCoversOutstanding(amounts: number[], outstandingCents: number): boolean {
  if (outstandingCents <= 0 || amounts.length === 0) return false;
  let sum = 0;
  for (const amount of amounts) {
    if (!Number.isInteger(amount) || amount <= 0) return false;
    sum += amount;
  }
  return sum <= outstandingCents;
}

export function isDeskOutstanding(input: {
  collection: string;
  outstandingCents: number;
}): boolean {
  return input.collection === 'desk' && input.outstandingCents > 0;
}

function asRow(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asNumber(value: unknown): number {
  return typeof value === 'number' ? value : Number(value ?? 0);
}

export function parseReceptionDesk(data: unknown): ReceptionDesk {
  const root = asRow(data);
  const queueRaw = Array.isArray(root?.queue) ? root.queue : [];
  const awaitingRaw = Array.isArray(root?.awaiting) ? root.awaiting : [];
  const queue: PaymentQueueRow[] = [];
  for (const item of queueRaw) {
    const row = asRow(item);
    if (!row || typeof row.appointment_id !== 'string' || typeof row.invoice_id !== 'string') {
      continue;
    }
    queue.push({
      appointment_id: row.appointment_id,
      invoice_id: row.invoice_id,
      invoice_number: asString(row.invoice_number),
      invoice_status: asString(row.invoice_status) ?? 'issued',
      service_name: asString(row.service_name),
      completed_at: asString(row.completed_at),
      starts_at: asString(row.starts_at) ?? '',
      outstanding_cents: asNumber(row.outstanding_cents),
      total_cents: asNumber(row.total_cents),
      due_at: asString(row.due_at),
      patient_name: asString(row.patient_name),
    });
  }
  const awaiting: AwaitingCompletionRow[] = [];
  for (const item of awaitingRaw) {
    const row = asRow(item);
    if (!row || typeof row.appointment_id !== 'string') continue;
    awaiting.push({
      appointment_id: row.appointment_id,
      service_name: asString(row.service_name),
      starts_at: asString(row.starts_at) ?? '',
      ends_at: asString(row.ends_at) ?? '',
      payment_status: asString(row.payment_status),
      patient_name: asString(row.patient_name),
    });
  }
  return { queue, awaiting };
}
